import {Table, TableHead, TableRow, TableCell, TableBody, styled, InputBase} from '@mui/material';
import NumberFormatCustom from 'helpers/NumberFormatter';
import {useTranslation} from 'react-i18next';
import {format} from 'date-fns';
import useLanguage from '../../hooks/useLanguage';
import {GrowthModelResponseAdjustmentTypeEnum} from 'client';

export declare type MonthlyAdjustment = number | undefined;

const dimensionWidth = 90;
export const MonthsSinceReleaseTable = ({
  adjustmentType,
  adjustments,
  onChange,
}: {
  adjustmentType: GrowthModelResponseAdjustmentTypeEnum;
  adjustments: MonthlyAdjustment[];
  onChange: (newAdjustments: MonthlyAdjustment[]) => void;
}) => {
  const {t} = useTranslation('common');
  const isCalendarYear = adjustmentType === GrowthModelResponseAdjustmentTypeEnum.CalendarYear;
  return (
    <Table>
      <TableHead>
        <TableRow>
          {isCalendarYear && <YearlyAdjustmentsTableCell>{t('year')}</YearlyAdjustmentsTableCell>}
          <MonthlyAdjustmentsTableCell width={isCalendarYear ? dimensionWidth : undefined}>
            {isCalendarYear ? t('month') : t('monthsAfterStocking')}
          </MonthlyAdjustmentsTableCell>
          <MonthlyAdjustmentsTableCell>{t('adjustment')} %</MonthlyAdjustmentsTableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {adjustments.map((adjustment, i) => (
          <AdjustmentRow {...{isCalendarYear, adjustment, i, adjustments, onChange}} />
        ))}
      </TableBody>
    </Table>
  );
};

const AdjustmentRow = ({
  isCalendarYear,
  adjustment,
  i,
  adjustments,
  onChange,
}: {
  adjustment: MonthlyAdjustment;
  i: number;
  isCalendarYear: boolean;
  adjustments: MonthlyAdjustment[];
  onChange: (newAdjustments: MonthlyAdjustment[]) => void;
}) => {
  const {datefnsLocale} = useLanguage();
  const {t} = useTranslation('common');
  const period = translateIndexToDate(i, isCalendarYear, datefnsLocale);
  const borderBottom = period.monthNumber === 11 ? undefined : 0;
  return (
    <TableRow>
      {isCalendarYear && (
        <MonthlyAdjustmentsTableCell sx={{borderBottom}}>
          {' '}
          {period.yearNumber && `${t('year')} ${period.yearNumber}`}
        </MonthlyAdjustmentsTableCell>
      )}
      <MonthlyAdjustmentsTableCell>{period.month}</MonthlyAdjustmentsTableCell>
      <MonthlyAdjustmentsTableCell>
        <InputBase
          inputComponent={NumberFormatCustom as (props) => JSX.Element}
          onClick={({target}) => {
            const inputField = target as HTMLInputElement;
            if ('select' in inputField && typeof inputField.select === 'function') {
              inputField.select();
            }
          }}
          onChange={({target: {value}}) => {
            const newValue = value.length === 0 ? undefined : parseInt(value);
            if (newValue === undefined || !isNaN(newValue)) {
              const newAdjustments = [...adjustments];
              newAdjustments[i] = newValue;
              onChange(newAdjustments);
            }
          }}
          onBlur={() => {
            if (adjustments[i] === undefined) {
              const newAdjustments = [...adjustments];
              newAdjustments[i] = 0;
              onChange(newAdjustments);
            }
          }}
          value={adjustment}
          inputProps={{'data-testid': `adjustment-${i}`}}
        />
      </MonthlyAdjustmentsTableCell>
    </TableRow>
  );
};

const YearlyAdjustmentsTableCell = styled(TableCell)(() => ({
  padding: '0 8px',
  width: dimensionWidth,
}));

interface StyledTableCellProps {
  width?: number;
}

const MonthlyAdjustmentsTableCell = styled(TableCell)<StyledTableCellProps>(({width}) => ({
  padding: '0 8px',
  width,
}));

const translateIndexToDate = (
  index: number,
  isCalendarYear: boolean,
  locale: Locale,
): {yearNumber?: number; month: string | number; monthNumber?: number} => {
  if (isCalendarYear) {
    const startYear = 1;
    const yearNumber = Math.floor(index / 12) + startYear;
    const month = index % 12;
    const year = 2020 + yearNumber;
    const date = new Date(year, month, 1);
    const yearNumberDisplay = date.getMonth() === 0 ? yearNumber : undefined;
    return {
      yearNumber: yearNumberDisplay,
      month: format(date, 'MMMM', {locale}),
      monthNumber: month,
    };
  } else {
    return {month: index};
  }
};
